import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { Image } from "primereact/image";

import lightbulb from "../../../assets/images/lightbulb.png";
import FloatLabelInput from "../../../components/FloatingLabel";
import useStoreHealthQuestionnaire from "../../../store/actions/store-health-questionnaire";
import useGetHealthQuestionnaire from "../../../store/selectors/get-health-questionnaire";
import BottomMessagePopUp from "../../../components/BottomMessagePopUp";
import useGetQuoteHealthDetails from "../../../store/selectors/get-quote-health-details";
import { createHealthEntry, createHealthManualEntry } from "../../../utils/backend";
import useGetToken from "../../../store/selectors/get-token";
import useGetHealthPlan from "../../../store/selectors/get-health-plan-quote";
import useStoreOrderDetailsId from "../../../store/actions/store-order-details";
import { useTranslation } from 'react-i18next';

const HealthQuestionnaire = () => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	// const [weight, setWeight] = useState("");
	// const [height, setHeight] = useState("");
	const navigate = useNavigate();
	const storeHealthQuestionnaire = useStoreHealthQuestionnaire();
	const getHealthQuestionnaire = useGetHealthQuestionnaire();
	const token = useGetToken();
	const getQuoteHealthDetails = useGetQuoteHealthDetails();
	const healthPlanQuote = useGetHealthPlan();
	const [errorMessage, setErrorMessage] = useState("");
	const [isErrorVisible, setIsErrorVisible] = useState(false);
	const showErrorMessage = (msg) => {
		setErrorMessage(msg);
		setIsErrorVisible(true);
	};
	const [formData, setFormData] = useState({
        weight: "",
        height: "",
        healthy: "",
        history: "",
        medication: "",
        physicalDefect: "",
        insuranceDeclined: "",
        isOtherInsurance: "",
        otherInsurance: "",
    });
	const storeOrderDetailsId = useStoreOrderDetailsId();

	const submitSelectedPlan = async (e) => {
		e.preventDefault();
		setLoading(true);
		if (
			formData.weight === "" ||
			formData.height === "" ||
			formData.healthy === "" ||
			formData.history === "" ||
			formData.medication === "" ||
			formData.physicalDefect === "" ||
			formData.insuranceDeclined === "" ||
			formData.isOtherInsurance === "" ||
			(formData.isOtherInsurance === "yes" && formData.otherInsurance === "")
		) {
			showErrorMessage(`${t("messages.fillInAllFields")}`);
			setLoading(false);
			return;
		}

		const dateOfBirth = new Date(getQuoteHealthDetails.dob);
		dateOfBirth.setHours(dateOfBirth.getHours() + 8);
		const formattedDateOfBirth = dateOfBirth.toISOString().split("T")[0];

		const payloads = {
			full_name: getQuoteHealthDetails?.policy_holder_name,
			insurance_plan: healthPlanQuote?.plan_name,
			insurer_name: healthPlanQuote?.company_name,
			//order_value: healthPlanQuote?.premium,
			product_code: healthPlanQuote?.sku_code,
			product_id: healthPlanQuote?.id,
			selected_plan_code: healthPlanQuote?.sku_code,
			//quoted_amount: healthPlanQuote?.premium,
			gender: getQuoteHealthDetails?.gender,
			date_of_birth: formattedDateOfBirth,
			occupation: getQuoteHealthDetails?.occupation,
			age: getQuoteHealthDetails?.age,
			company_name: getQuoteHealthDetails?.ph_company_name,
			height: formData.height,
			weight: formData.weight,
			is_true_1a: formData.healthy === "no" ? 0 : 1,
			is_true_1b: formData.history === "no" ? 0 : 1,
			is_true_1c: formData.medication === "no" ? 0 : 1,
			is_true_2a: formData.physicalDefect === "no" ? 0 : 1,
			is_true_2b: formData.insuranceDeclined === "no" ? 0 : 1,
			is_true_2c: formData.isOtherInsurance === "no" ? 0 : 1,
			id_type: getQuoteHealthDetails?.id_type,
			id_no: getQuoteHealthDetails?.id_no,
			room_board: getQuoteHealthDetails?.room_board,
			is_smoker: getQuoteHealthDetails?.is_smoker === "no" ? 0 : 1,
			is_manual_work: getQuoteHealthDetails?.is_dangerous_expose === "no" ? 0 : 1,
		};

		if (formData.isOtherInsurance === "yes") {
			payloads.other_health_insurance = formData.otherInsurance;
		}

		if (
			formData.healthy === "yes" &&
			formData.history === "yes" &&
			formData.medication === "yes" &&
			formData.physicalDefect === "no" &&
			formData.insuranceDeclined === "no" &&
			formData.isOtherInsurance === "no"
		) {
			const response = await createHealthEntry(token, payloads);
			if (response.success) {
				storeOrderDetailsId(response?.data || null);
				if (getQuoteHealthDetails.id_type === "nric" || getQuoteHealthDetails.id_type === "NRIC") {
					navigate(`/get-health-quotes/verify-id`);
				} else if (getQuoteHealthDetails.id_type === "passport") {
					navigate(`/get-health-quotes/verify-passport`);
				}
				setLoading(false);
			}
		} else {
			const response = await createHealthManualEntry(token, payloads);
			if (response.success) {
				navigate(`/get-health-quotes/negative-submission`);
			} else {
				navigate(`/get-health-quotes/negative-submission-failed`);
			}
			setLoading(false);
		}
	};

	const handleChange = (field, value) => {
		const updatedFormData = { ...formData, [field]: value };
        setFormData(updatedFormData);

        // Update the store with the new form data
        storeHealthQuestionnaire(updatedFormData);
	};

	useEffect(() => {
        if (getHealthQuestionnaire) {
            setFormData(getHealthQuestionnaire);
        }
    }, [getHealthQuestionnaire]);

	return (
		<>
			<div className="content px-2">
				<div className="get-quote-process-container vw-50">
					<div className="d-flex flex-column">
						<h3 className="mb-0">{`${t("healthInsurance.questionnaires.title")}`}</h3>
						<div className="d-flex align-items-center w-100 mt-4">
							<Image className="mr-2" src={lightbulb} height={36} width={36} />
							<span className="formatted-text w-85">
								{`${t("healthInsurance.questionnaires.subTitle")}`}
							</span>
						</div>

						<p className="fw-bold color-heading">{`${t("healthInsurance.questionnaires.questions.1")}`}</p>
						<div>
							<div className="row justify-content-between mx-0">
								<div className="col-lg-6 col-sm-6 input-group">
									<FloatLabelInput
										label={
											<>
												{`${t("healthInsurance.questionnaires.weight")}`}
												<span className="required-asterisk">*</span>
											</>
										}
										value={formData.weight}
										onChange={(e) => handleChange("weight", e.target.value)}
										placeholder=""
									/>
								</div>

								<div className="col-lg-6 col-sm-6 input-group">
									<FloatLabelInput
										label={
											<>
												{`${t("healthInsurance.questionnaires.height")}`}
												<span className="required-asterisk">*</span>
											</>
										}
										value={formData.height}
										onChange={(e) => handleChange("height", e.target.value)}
										placeholder=""
									/>
								</div>
							</div>
							<p className="fw-bold color-heading">{`${t("healthInsurance.questionnaires.questions.2")}`}</p>
							<p className="text-justify color-heading my-0">
								{`${t("healthInsurance.questionnaires.questions.2a")}`}
								<span className="required-asterisk">*</span>
							</p>

							<div className="vw-100 d-flex flex-row my-3">
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="healthyYes"
										name="healthy"
										value="yes"
										onChange={(e) => handleChange("healthy", e.value)}
										checked={formData.healthy === "yes"}
									/>
									<label htmlFor="healthyYes" className="ml-2">
										{`${t("common.yes")}`}
									</label>
								</div>
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="healthyNo"
										name="healthy"
										value="no"
										onChange={(e) => handleChange("healthy", e.value)}
										checked={formData.healthy === "no"}
									/>
									<label htmlFor="healthyNo" className="ml-2">
										{`${t("common.no")}`}
									</label>
								</div>
							</div>
						</div>

						<div>
							<p className="text-justify color-heading my-0">
								{`${t("healthInsurance.questionnaires.questions.2b")}`}
								<span className="required-asterisk">*</span>
							</p>
							<div className="vw-100 d-flex flex-row my-3">
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="historyYes"
										name="history"
										value="yes"
										onChange={(e) => handleChange("history", e.value)}
										checked={formData.history === "yes"}
									/>
									<label htmlFor="historyYes" className="ml-2">
										{`${t("common.yes")}`}
									</label>
								</div>
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="historyNo"
										name="history"
										value="no"
										onChange={(e) => handleChange("history", e.value)}
										checked={formData.history === "no"}
									/>
									<label htmlFor="historyNo" className="ml-2">
										{`${t("common.no")}`}
									</label>
								</div>
							</div>
						</div>

						<div>
							<p className="text-justify color-heading my-0">
								{`${t("healthInsurance.questionnaires.questions.2c")}`}
								<span className="required-asterisk">*</span>
							</p>

							<div className="vw-100 d-flex flex-row my-3">
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="medicationYes"
										name="medication"
										value="yes"
										onChange={(e) => handleChange("medication", e.value)}
										checked={formData.medication === "yes"}
									/>
									<label htmlFor="medicationYes" className="ml-2">
										{`${t("common.yes")}`}
									</label>
								</div>
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="medicationNo"
										name="medication"
										value="no"
										onChange={(e) => handleChange("medication", e.value)}
										checked={formData.medication === "no"}
									/>
									<label htmlFor="medicationNo" className="ml-2">
										{`${t("common.no")}`}
									</label>
								</div>
							</div>
						</div>

						<div>
							<p className="fw-bold color-heading">{`${t("healthInsurance.questionnaires.questions.3")}`}</p>
							<p className="text-justify color-heading my-0">
								{`${t("healthInsurance.questionnaires.questions.3a")}`}
								<span className="required-asterisk">*</span>
							</p>

							<div className="vw-100 d-flex flex-row my-3">
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="physicalDefectYes"
										name="physicalDefect"
										value="yes"
										onChange={(e) => handleChange("physicalDefect", e.value)}
										checked={formData.physicalDefect === "yes"}
									/>
									<label htmlFor="physicalDefectYes" className="ml-2">
										{`${t("common.yes")}`}
									</label>
								</div>
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="physicalDefectNo"
										name="physicalDefect"
										value="no"
										onChange={(e) => handleChange("physicalDefect", e.value)}
										checked={formData.physicalDefect === "no"}
									/>
									<label htmlFor="physicalDefectNo" className="ml-2">
										{`${t("common.no")}`}
									</label>
								</div>
							</div>
						</div>

						<div>
							<p className="text-justify color-heading my-0">
								{`${t("healthInsurance.questionnaires.questions.3b")}`}
								<span className="required-asterisk">*</span>
							</p>

							<div className="vw-100 d-flex flex-row my-3">
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="insuranceDeclinedYes"
										name="insuranceDeclined"
										value="yes"
										onChange={(e) => handleChange("insuranceDeclined", e.value)}
										checked={formData.insuranceDeclined === "yes"}
									/>
									<label htmlFor="insuranceDeclinedYes" className="ml-2">
										{`${t("common.yes")}`}
									</label>
								</div>
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="insuranceDeclinedNo"
										name="insuranceDeclined"
										value="no"
										onChange={(e) => handleChange("insuranceDeclined", e.value)}
										checked={formData.insuranceDeclined === "no"}
									/>
									<label htmlFor="insuranceDeclinedNo" className="ml-2">
										{`${t("common.no")}`}
									</label>
								</div>
							</div>
						</div>

						<div>
							<p className="text-justify color-heading my-0">
								{`${t("healthInsurance.questionnaires.questions.3c")}`}
								<span className="required-asterisk">*</span>
							</p>

							<div className="vw-100 d-flex flex-row my-3">
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="isOtherInsuranceYes"
										name="isOtherInsurance"
										value="yes"
										onChange={(e) => handleChange("isOtherInsurance", e.value)}
										checked={formData.isOtherInsurance === "yes"}
									/>
									<label htmlFor="isOtherInsuranceYes" className="ml-2">
										{`${t("common.yes")}`}
									</label>
								</div>
								<div className="flex align-items-center col-lg-6 col-sm-6">
									<RadioButton
										inputId="isOtherInsuranceNo"
										name="isOtherInsurance"
										value="no"
										onChange={(e) => handleChange("isOtherInsurance", e.value)}
										checked={formData.isOtherInsurance === "no"}
									/>
									<label htmlFor="isOtherInsuranceNo" className="ml-2">
										{`${t("common.no")}`}
									</label>
								</div>
							</div>
						</div>
						{formData.isOtherInsurance === "yes" && (
							<div>
								<div className="vw-100 d-flex flex-row mb-3">
									<FloatLabelInput
										label={
											<>
												{`${t("healthInsurance.questionnaires.otherHealthInsurance")}`}
												<span className="required-asterisk">*</span>
											</>
										}
										value={formData.otherInsurance}
										onChange={(e) => handleChange("otherInsurance", e.target.value)}
										placeholder=""
									/>
								</div>
							</div>
						)}
						<Button
							label={`${t("common.proceed")} (2/7)`}
							className="btn-primary"
							loading={loading}
							onClick={(e) => submitSelectedPlan(e)}
						/>	
						{/* BottomMessagePopUp Component */}
						<BottomMessagePopUp
							message={errorMessage}
							type="error"
							isVisible={isErrorVisible}
							onClose={() => setIsErrorVisible(false)}
						/>				
					</div>
				</div>
			</div>
		</>
	);
};

export default HealthQuestionnaire;
